/**
 * Typed API contract — mirrors the elaborate backend `/api` surface EXACTLY.
 * Single origin, session-cookie auth. Consumed by both the real adapter (hits
 * `/api`) and the mock adapter (fixtures), selected via VITE_USE_MOCK.
 */

// ── auth ────────────────────────────────────────────────────────────────
export interface User {
  id: string;
  name: string;
  email: string;
  is_staff: boolean;
}

export interface Membership {
  workspace_slug: string;
  role: string;
}

export interface MeResponse {
  user: User;
  memberships: Membership[];
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  /** Present on a completed single-factor login. */
  user?: User;
  /** Set when the account has TOTP enrolled: the password step succeeded but a
   *  second factor is required. The client then calls POST /auth/totp/verify. */
  mfa_required?: boolean;
}

// ── export + version history ──────────────────────────────────────────────
export type ExportFormat = "docx" | "pdf";

/** The export render template. `cerebr8` = the Cerebr8 delivery brand (Alexandria
 *  + navy/orange + cover + running header/footer); `plain` = the unbranded raw
 *  rendition. Mirrors the backend `?template=` grammar. */
export type ExportTemplate = "cerebr8" | "plain";

/** The Content-Disposition posture. `attachment` = download; `inline` = render in
 *  the browser (the `<iframe>` PDF preview). Same bytes either way. */
export type ExportDisposition = "attachment" | "inline";

/** Optional export URL modifiers (§1.3). Omitting all keeps the legacy URL
 *  (back-compat: `exportUrl(id, fmt)` === `?format=fmt`). */
export interface ExportOptions {
  template?: ExportTemplate;
  disposition?: ExportDisposition;
}

/** The single-origin export URL for a document — the SAME builder the mock + real
 *  adapters use so their URLs are byte-identical. Back-compat: no `opts` yields
 *  the legacy `?format=<fmt>`; `template`/`disposition` append in that order. */
export function exportUrlFor(
  docId: string,
  format: ExportFormat,
  opts?: ExportOptions,
): string {
  const params = new URLSearchParams({ format });
  if (opts?.template) params.set("template", opts.template);
  if (opts?.disposition) params.set("disposition", opts.disposition);
  return `/api/documents/${encodeURIComponent(docId)}/export?${params.toString()}`;
}

/** One entry in a document's version history
 *  (GET /api/documents/:id/versions). A point-in-time snapshot of the rendered
 *  content, with the author + note captured at record time. */
/** Avatar attribution actor discriminator. Human users render as a CIRCLE
 *  avatar, AI agents as a SQUARE-with-soft-corners avatar, workspaces as a
 *  rounded-square badge, system as the ◆ badge. */
export type ActorType = "user" | "agent" | "workspace" | "system";

/** The canonical actor reference the server embeds on every attributed row
 *  (version / comment / activity / note). The `<Avatar>` component consumes it
 *  uniformly. `avatar_url` is null when the actor has no uploaded image → the
 *  client paints the deterministic initials+color fallback keyed off (type,id).
 *  Optional on every read type so a mid-migration server that hasn't populated
 *  it still yields valid JSON and the client falls back to the legacy `*_name`
 *  string. */
export interface ActorRef {
  id: number;
  type: ActorType;
  name: string;
  avatar_url: string | null;
}

export interface DocVersionMeta {
  id: number;
  document_id: number;
  note: string;
  editor_name: string;
  /** The resolved version editor (avatar attribution). Absent on a legacy
   *  server → fall back to `editor_name` wrapped as a `user` ref. */
  editor?: ActorRef;
  content_sha256: string;
  created_at: string;
  /**
   * **THIS REVISION IS A REVERT**, and this is the id of the revision it put
   * back — `0` for an ordinary revision, and ABSENT from a server that predates
   * the field. Mirrors `models.DocVersionMeta.restored_from_version_id`.
   *
   * Optional, and every reader must treat `undefined` and `0` alike: a
   * truthiness test written the obvious way (`if (v.restored_from_version_id)`)
   * is correct, but `!== 0` would mark every revision from a legacy server as a
   * revert of `undefined`.
   */
  restored_from_version_id?: number;
}
export interface DocVersionDetail extends DocVersionMeta {
  rendered_html: string;
}

/**
 * What a version RESTORE moved (`POST /api/documents/:id/versions/:vid/restore`).
 *
 * A restore rewrites the head of a delivered document with bytes nobody just
 * typed, so the terminal does not answer "ok" — it answers with everything that
 * changed, measured server-side from the rows before and after the write. The
 * two hashes answer different questions and a restore routinely moves one
 * without the other: `head_hash_*` is the section-derived MEANING head;
 * `content_hash_*` is the delivery-gate hash every reviewer verdict is pinned
 * to. Mirrors `models.VersionRestoreReport` key-for-key.
 */
export interface VersionRestoreReport {
  document_id: number;
  restored_from_version_id: number;
  /** Snapshot of the pre-restore head, minted BEFORE the write (0 if empty). */
  backup_version_id: number;
  doc_version_id: number;
  head_version_id_before: number;
  head_version_id_after: number;
  head_hash_before: string;
  head_hash_after: string;
  content_hash_before: string;
  content_hash_after: string;
  content_hash_changed: boolean;
  bytes_before: number;
  bytes_after: number;
  /** Did the stored bytes actually change? `false` ⇒ the restore was inert. */
  bytes_written: boolean;
  authoring_source: "rendered_html" | "body_md" | "rendered_html_only";
  /** Non-empty when the restore was degraded in a way the operator must know. */
  warning: string;
  verdicts_invalidated: string[];
  verdicts_carried: string[];
  proposals_staled: number;
  changed_sections: { section_id: number; seq: number; change_kind: string; heading: string }[];
}

// ── invites ─────────────────────────────────────────────────────────────
export interface InviteCreateRequest {
  email: string;
  workspace_slug: string;
  role: string;
}

export interface InviteCreateResponse {
  /** relative link, e.g. "/set-password?token=…" */
  invite_url: string;
}

export interface InviteInfo {
  email: string;
  workspace_name: string;
}

export interface InviteAcceptRequest {
  name: string;
  password: string;
}

export interface InviteAcceptResponse {
  user: User;
}

// ── workspaces ──────────────────────────────────────────────────────────
export type DocStatus = "delivered" | "draft" | "superseded";

export interface DocCounts {
  delivered: number;
  draft: number;
  superseded: number;
}

export interface WorkspaceSummary {
  slug: string;
  name: string;
  client: string;
  accent: string;
  doc_counts: DocCounts;
}

export interface Folder {
  id: string;
  name: string;
  doc_count: number;
}

export interface WorkspaceDetail {
  slug: string;
  name: string;
  client: string;
  accent: string;
  folders: Folder[];
  /** Every LIVE (non-archived) document in the workspace — foldered AND
   *  folderless. The folder breakdown (`folders[].doc_count`) omits root/
   *  folderless docs, so summing it undercounts; the "All documents" library node
   *  renders THIS instead (it matches the header's full-list count). */
  total_documents: number;
}

// ── documents ───────────────────────────────────────────────────────────
export interface DocumentSummary {
  id: string;
  title: string;
  folder: string;
  format: string;
  category: string;
  status: DocStatus;
  brand: string;
  lang: string;
  delivered_at: string | null;
  has_evidence: boolean;
}

export interface Verdict {
  reviewer: string;
  /**
   * A STORED token (`pass` / `fail`) held at the head hash, or the read-time
   * token `pending` when this required reviewer holds no verdict there at all.
   * `pending` is never written to the row store — it is the server's honest
   * rendering of an absence.
   */
  verdict: string;
}

/**
 * The delivery-evidence bundle, DERIVED BY THE SERVER at read time from
 * `el.review_verdicts` against the current head content hash — the same
 * computation the export gate enforces.
 *
 * It is not a stored projection. The previous `el.delivery_evidence` row was
 * written once at seed time and never re-derived, so the panel kept rendering
 * five green PASS chips on documents whose export was 403-ing. Architecture
 * invariant 6 requires the gate be server-derived and never assertable in prose
 * or from the client; `blocked` is therefore the SERVER's verdict on the gate,
 * not something this client recomputes from `verdicts`.
 */
export interface Evidence {
  hash: string;
  verdicts: Verdict[];
  /** `true` ⇔ export would be blocked right now (a required reviewer has not passed on `hash`). */
  blocked: boolean;
}

/** Why an export would be refused. Mirrors `api.export_policy.reason_*`. */
export type ExportGateReason = "ok" | "forbidden" | "blocked";

/**
 * THE SERVER'S ANSWER to "would an export render bytes right now?", derived by
 * the SAME `api.export_policy.decide` the export terminal enforces with.
 *
 * It exists because `GET /api/documents/:id/export` refuses with a JSON body,
 * and a native `<a download>` CANNOT INSPECT A STATUS CODE — the browser writes
 * whatever arrives to disk under the requested name, so a 403 was saved as
 * `Report.pdf`. This lets the download menu withdraw the affordance and name the
 * reason before anything is clicked.
 *
 * NEGATIVE-ONLY. `permitted: false` removes a button. `permitted: true`
 * authorises nothing — the terminal re-decides on every request (architecture
 * invariant 6: the delivery gate is server-derived and never assertable from the
 * client). Never treat this as permission; treat it as an affordance hint.
 */
export interface ExportGate {
  permitted: boolean;
  reason: ExportGateReason;
  /** Required reviewers still lacking a `pass` at the head hash; `blocked` only. */
  missing: string[];
}

export interface DocumentDetail extends DocumentSummary {
  rendered_html: string;
  evidence: Evidence | null;
  /**
   * `null` only from a server that predates the projection — the client treats
   * an absent gate as "offer the download and let the guarded fetch catch a
   * refusal", which is exactly the pre-projection behaviour plus the safety net.
   */
  export_gate: ExportGate | null;
}

// ════════════════════════════════════════════════════════════════════════
// Wave-2 P2 feature contracts
// ════════════════════════════════════════════════════════════════════════

// ── governance (agent defs + guidelines — view/edit) ──────────────────────
export type GovernanceCategory = "agent" | "guide";
export interface GovernanceDoc {
  id: number;
  title: string;
  category: GovernanceCategory;
  source_ref: string;
  version_count: number;
  updated_at: string | null;
  updated_by: string | null;
}
export interface GovernanceVersion {
  id: number;
  body: string;
  edited_by: string | null;
  created_at: string;
}
export interface GovernanceDetail {
  id: number;
  title: string;
  category: GovernanceCategory;
  source_ref: string;
  body: string;
  versions: GovernanceVersion[];
}
export interface GovernanceSaveRequest {
  body: string;
}

// ── activity (audit trail / feed) ─────────────────────────────────────────
// IA article-first sweep: `ActivityEvent` gains the flat `ArticleCtx` fields —
// present when the event's document belongs to an article-unit, so the row
// deep-links into the workbench (`/w/:slug/article/:unitId?show=<role>`). All
// optional/nullable: invite/join/system + loose-doc events keep the null/""
// defaults and render exactly as before. The integrator promotes these four to a
// shared `ArticleCtx` interface reused by SearchHit/Ticket/PersonMembership/
// ProposedRevision (see the IA spec's shared-file reconciliation table).
export interface ActivityEvent {
  id: number;
  verb: string;
  object_type: string;
  object_id: number | null;
  summary: string;
  actor_name: string | null;
  /** The resolved event actor (avatar attribution), or null for a pure system
   *  event. Absent on a legacy server → fall back to `actor_name`. */
  actor?: ActorRef | null;
  created_at: string;
  /** Owning article-unit id, or null for a loose/legacy/system event. */
  article_unit_id?: number | null;
  /** Document role within its unit ('brief'|'research'|'article'); "" when loose. */
  resource_role?: string;
  /** Owning unit's display title, or null when unattached. */
  unit_title?: string | null;
  /** Owning unit's slug (the article-unit slug, NOT the workspace slug). */
  unit_slug?: string | null;
}
export interface ActivityQuery {
  before?: number;
  limit?: number;
  /** Restrict to one article-unit's events (per-unit feed deep link). */
  unit?: number;
  /** `"articles"` narrows the feed to article-unit events; omit for all. */
  scope?: "articles";
}

// ── notes (per-subject research notes) ────────────────────────────────────
export interface NoteSummary {
  id: number;
  document_id: number | null;
  title: string;
  excerpt: string;
  author_name: string;
  /** The resolved note author (avatar attribution) — always a `user` ref. */
  author?: ActorRef;
  created_at: string;
  updated_at: string;
}
export interface Note {
  id: number;
  workspace_slug: string;
  document_id: number | null;
  body: string;
  body_html: string;
  title: string;
  author_name: string;
  /** The resolved note author (avatar attribution) — always a `user` ref. */
  author?: ActorRef;
  created_at: string;
  updated_at: string;
}
export interface NoteCreate {
  title: string;
  body: string;
  document_id?: number | null;
}
export interface NoteUpdate {
  title: string;
  body: string;
}

// ── document upload (clients/staff upload briefings) ──────────────────────
export interface UploadDocumentRequest {
  title: string;
  folder: string;
  format: string;
  filename: string;
  lang: string;
  content_b64: string;
}
export interface UploadDocumentResponse {
  document: DocumentSummary;
}

// ── folder management (staff) ─────────────────────────────────────────────
export interface FolderNameRequest {
  name: string;
}
export interface FolderMutationResponse {
  folder: Folder;
}

// ── global search (ARTICLE-UNIT-FIRST · IA sweep §5) ──────────────────────
// GET /api/workspaces/:slug/search?q= → `SearchResults`. A hit in ANY of an
// article unit's current-head resources (brief/research/article) surfaces the
// UNIT ONCE (`units`), never one row per resource and never the superseded
// version history. Documents that belong to no unit come back under `other`.

/** One matched ARTICLE UNIT. Rendered with the SAME `<ArticleUnitRow>` card the
 *  Articles landing uses (title + brief/research/article chips + status),
 *  drilling into `/w/:slug/article/:id`. */
export interface SearchUnitHit {
  id: string;
  title: string;
  slug: string;
  status: DocStatus;
  brand: string;
  lang: string;
  /** How many of the 3 role resources (brief/research/article) the unit has. */
  resource_count: number;
  /** Which roles matched the query, comma-joined ("article,brief"); "" when the
   *  unit matched only by its title. */
  matched_roles: string;
  /** One representative snippet (highest-priority matching resource). */
  snippet: string;
}

/** One LOOSE / legacy document (belongs to no article unit) — the secondary
 *  "Other documents" section, drilling into the flat reader (`/doc/:id`). */
export interface SearchHit {
  id: string;
  title: string;
  folder: string;
  status: DocStatus;
  snippet: string;
}

/** The whole search response: article-unit hits first, loose documents second. */
export interface SearchResults {
  units: SearchUnitHit[];
  other: SearchHit[];
}

// ── settings ──────────────────────────────────────────────────────────────
export interface WorkspaceSettings {
  slug: string;
  name: string;
  accent: string;
}
export interface WorkspaceSettingsUpdate {
  name: string;
  accent: string;
}
// The per-workspace editor-toolbar governance model. Re-exported from
// `@norkron/web-elaborate-editor` so the API layer + settings UI + editor mount
// all share the ONE registry-driven type (never a divergent hand-rolled mirror —
// RULE ZERO). The server persists it in `el.settings(key='editor.toolbar')`.
export type { EditorToolbarConfig } from "@norkron/web-elaborate-editor";
export interface MeUpdate {
  name: string;
}

// ── workspace management (staff) ──────────────────────────────────────────
export interface WorkspaceCreateRequest {
  slug: string;
  name: string;
  client?: string;
  accent?: string;
}
export interface RoleChangeRequest {
  role: string;
}

// ── tickets (cross-document review queue) ─────────────────────────────────
export type TicketType = "comment" | "suggestion" | "request-for-change";
export type TicketStatus =
  | "open"
  | "resolved"
  | "acted_on"
  | "awaiting_re_review"
  | "pinned"
  | "orphaned";
/**
 * THE anchor-state vocabulary — exactly what the server can put on the wire, and
 * nothing it cannot.
 *
 * The first four are `docz`'s `RebaseOutcome`, written by the commit-time
 * `docz.anchor.rebase` into `el.comment_anchors.anchor_state`. The fifth,
 * `unanchored`, is the disjoint case where there is NO anchor row at all: the
 * quote never resolved to a carved section, so there is nothing to grade. Both
 * server read models project it through one shared expression
 * (`models.anchor_state_sql` = `COALESCE(ca.anchor_state,'unanchored')`), so the
 * tickets grid and the comment gutter cannot disagree.
 *
 * ⛔ This union used to read `"exact" | "moved" | "orphaned"`, which was wrong in
 * BOTH directions: `moved` is a value no server surface has ever emitted, while
 * `shifted`, `repaired` and `unanchored` — all of which it does emit — fell
 * outside the type. Everything that fell outside took the `anchorIsFlagged`
 * false branch, and `Tickets.tsx` renders that branch as the literal word
 * "Anchored". A comment whose anchor never resolved was labelled ANCHORED on
 * screen. Widening this type is what makes that unrepresentable: `ANCHOR_LABEL`
 * and `ANCHOR_FLAGGED` in `lib/tickets.ts` are exhaustive `Record`s over it, so
 * a sixth state cannot be added without a deliberate decision at each.
 */
export type AnchorState =
  | "exact"
  | "shifted"
  | "repaired"
  | "orphaned"
  | "unanchored";
export interface Ticket {
  id: number;
  document_id: string;
  doc_title: string;
  type: TicketType;
  status: TicketStatus;
  quote: string;
  body: string;
  block_ref: string;
  anchor_state: AnchorState;
  author_name: string;
  created_at: string;
  // ── article-unit context (design §5 IA sweep · shared `ArticleCtx`) ───────
  // Additive-optional: surfaced by the tickets query's `LEFT JOIN
  // el.article_units`. A loose/legacy ticket (document never carved into a
  // unit) carries `article_unit_id: null` and the row falls back to `/doc/:id`.
  article_unit_id?: number | null;
  resource_role?: string;
  unit_title?: string | null;
  unit_slug?: string | null;
}
export interface TicketQuery {
  type?: TicketType | "all";
  status?: TicketStatus | "all";
  /** Scope the queue to one article-unit (the workbench "N tickets" chip). */
  unit?: number;
}

// ── proposals (agent-proposed revisions — the human-approval gate) ────────
export type ProposalStatus = "pending" | "accepted" | "rejected";
/** The article-unit resource slot a revised document occupies. `""` marks a
 *  loose/legacy document that belongs to no article unit (the pre-sweep world). */
export type ProposalResourceRole = "brief" | "research" | "article" | "";
export interface ProposedRevision {
  id: number;
  document_id: number;
  document_title: string;
  block_ref: string;
  base_body: string;
  proposed_body: string;
  rationale: string;
  author_agent: string;
  status: ProposalStatus;
  created_at: string;
  decided_by: string | null;
  decided_at: string | null;
  // ── article-unit context (IA article-first sweep, design §3) ──────────────
  // Additive-optional: the unit the revised document belongs to, so the card can
  // render "revises <role> of <unit_title>" and link into the workbench at
  // /w/:slug/article/:unitId?show=<role>. `null`/`""` for a loose/legacy doc →
  // the card falls back to the bare document title + /doc/:id link (no regression).
  // Backend: proposals_repo.ProposedRevision (LEFT JOIN el.article_units, zero migration).
  article_unit_id?: number | null;
  resource_role?: ProposalResourceRole;
  unit_title?: string | null;
  unit_slug?: string | null;
}
export interface ProposalCreate {
  block_ref: string;
  base_body: string;
  proposed_body: string;
  rationale: string;
  author_agent: string;
}
export interface ProposalResponse {
  proposal: ProposedRevision;
}

// ── review comments (the full model — brief §6/§7) ────────────────────────
/** The three comment types (design-system §1.3). */
export type CommentType = "comment" | "suggestion" | "request-for-change";
/** The FIVE human-settable review statuses — the exact subset the server
 *  `validStatus` allowlist accepts (handlers/comments.zig) and the kanban's
 *  droppable columns + "Move to…" dropdown mirror. `awaiting_re_review`
 *  ("Re-review") sends a note back for another pass. `orphaned` is NOT here: it
 *  is the one system-derived `TicketStatus` (a thread whose quote no longer
 *  resolves), never settable from the client. */
export type CommentStatus =
  | "open"
  | "resolved"
  | "acted_on"
  | "awaiting_re_review"
  | "pinned";
/** comment-visibility (§2.3): a comment/note's scope. `public` (default —
 *  everyone on the document), `internal` (Cerebr8 staff only — keys off the
 *  global `is_staff` flag), `shared` (an explicit named audience), `private`
 *  (the author only). The read filter is server-side (the security boundary);
 *  the UI never hides anything by itself. */
export type CommentVisibility = "public" | "internal" | "shared" | "private";

/** The durable selector geometry (from web-elaborate-editor `buildSelector` + a
 *  client `quote_hash`) — everything needed to re-anchor a comment. */
export interface AnnotationDraft {
  block_ref: string;
  quote: string;
  prefix: string;
  suffix: string;
  start: number;
  end: number;
  quote_hash: string;
}

/** One resolved @-mention (links a chip to a workspace member). */
export interface MentionRef {
  user_id: number;
  name: string;
}

/** review-notes §4.3: one reactor inside a reaction group's who-list (id + display
 *  name → the who-reacted popup renders their avatar + name). */
export interface ReactionUser {
  user_id: number;
  name: string;
}

/** review-notes §4.3: one emoji's aggregate reaction on a comment — the distinct
 *  `emoji` grapheme, its `count`, whether the VIEWER reacted (`reacted_by_me` →
 *  the toggle-color chip), and the visibility-respecting `who` list (only ever the
 *  reactors of a comment the viewer can already see). Field names mirror the wire
 *  contract (`src/db/models.zig` `ReactionGroup`). */
export interface ReactionGroup {
  emoji: string;
  count: number;
  reacted_by_me: boolean;
  who: ReactionUser[];
}

/** PUT/DELETE /api/comments/:cid/reactions/:emoji → the updated group for that
 *  emoji ({comment_id, reaction}). A DELETE that empties the group echoes
 *  `count: 0` so the client drops the chip. */
export interface ReactionToggleResponse {
  comment_id: number;
  reaction: ReactionGroup;
}

/** A workspace member surfaced to the @-mention combobox (id/name/email). */
export interface WorkspaceMember {
  id: number;
  name: string;
  email: string;
}

/** POST /api/documents/:id/comments body: the anchor + rich body + type +
 *  the user ids to @-mention. */
export interface CommentCreate extends AnnotationDraft {
  body: string;
  /** rich-comments §4.2: `docz-fragment` (rich, encoded `DoczFragment`) or omit
   *  for legacy `markdown`. The server validates + defaults to `markdown`. */
  body_format?: string;
  type: CommentType;
  mentions: number[];
  /** comment-visibility (§2.3): the note's scope at creation. Omit ⇒ `public`
   *  (server default). */
  visibility?: CommentVisibility;
  /** The explicit audience (workspace-member ids), honored only when
   *  `visibility === "shared"` (each id is validated against membership). */
  shared_with?: number[];
}
/** Kept for the Phase-2a call sites. */
export type AnnotationCreate = CommentCreate;

/** POST /api/comments/:cid/replies body: a rich body + mentions, no anchor. */
export interface ReplyCreate {
  body: string;
  /** rich-comments §4.2: `docz-fragment` for a rich reply, else legacy markdown. */
  body_format?: string;
  mentions: number[];
}

/** A persisted comment as returned by the API. A THREAD ROOT carries the anchor
 *  (`parent_id === null`); a REPLY sets `parent_id` and leaves the anchor empty.
 *  `body` is the rich-text markdown source (`body_format`). */
export interface Comment extends AnnotationDraft {
  id: number;
  parent_id: number | null;
  author_id: number;
  type: CommentType;
  body_format: string;
  body: string;
  status: CommentStatus;
  author_name: string;
  /** The resolved comment author (avatar attribution). Absent on a legacy
   *  server → fall back to `author_name`/`author_id` wrapped as a `user` ref. */
  author?: ActorRef;
  created_at: string;
  /** review-notes §3.3: the content-last-changed time, bumped ONLY on an author
   *  body edit (never on status/visibility change). Optional on the wire so a
   *  server that hasn't shipped the `updated_at` column stays valid; read sites
   *  derive `edited = updated_at != null && updated_at !== created_at` and the
   *  Modified sort falls back to `created_at` when absent. */
  updated_at?: string;
  mentions: MentionRef[];
  /** comment-visibility (§2.3): the THREAD ROOT's scope (replies inherit — a
   *  reply's own value is a `public` placeholder; render the root's pill). The
   *  server always emits it (default `public`); optional on the wire type so
   *  legacy fixtures/producers stay valid — read sites default to `public`. */
  visibility?: CommentVisibility;
  /** The resolved `shared` audience (`{user_id, name}`) — non-empty only for a
   *  `shared` root; drives the "Shared · N" pill count + the chip names. */
  shared_with?: MentionRef[];
  /** review-notes §4: the per-emoji reaction groups on this comment (folded into
   *  the read, visibility-respecting). Optional on the wire so a legacy server /
   *  fixture stays valid; read sites default to `[]` (no reactions). */
  reactions?: ReactionGroup[];
  /** comment-version binding (`el.annotations.version_id` → `el.doc_versions.id`):
   *  the DOCUMENT version this note was written against. It is the SAME id the
   *  workbench timeline's `VersionTick.versionId` carries (stringified), so the
   *  card resolves a note's version against a tick list the client already holds
   *  — never a second version vocabulary.
   *
   *  `null`/absent = no binding (every legacy comment). The card then treats the
   *  note as CURRENT and shows no version ribbon: an unknown version must never
   *  be able to make a live note look obsolete. */
  version_id?: number | null;
  /** comment-delete: this is a TOMBSTONE — a deleted comment the server kept ONLY
   *  because living replies hang off it, so the thread does not orphan. Its
   *  content is already blanked server-side; the card renders a content-free
   *  "Comment deleted" placeholder.
   *
   *  A deleted comment with nothing living below it never reaches the wire at all
   *  (the server drops it), so `deleted === true` here ALWAYS means "placeholder
   *  holding a live thread together" — never "a deleted note leaked". */
  deleted?: boolean;
}
/** The comment concept was `Annotation` in Phase-2a; anchoring.ts imports it. */
export type Annotation = Comment;

/** POST /api/documents/:id/comments and /replies → { comment }. */
export interface CommentResponse {
  comment: Comment;
}

/** PATCH /api/comments/:cid/status → { id, status }. */
export interface StatusChange {
  id: number;
  status: CommentStatus;
}

/** review-notes §3: author body edit. PATCH /api/comments/:cid body — mirrors the
 *  create/reply body shape (opaque rich fragment + its format discriminator). */
export interface CommentBodyEdit {
  body: string;
  body_format: string;
}
/** PATCH /api/comments/:cid → the re-echoed body + the bumped `updated_at`. */
export interface CommentEditResponse {
  id: number;
  body: string;
  body_format: string;
  updated_at: string;
}

/** comment-visibility (§3.6): PATCH /api/comments/:cid/visibility body. */
export interface VisibilityChange {
  visibility: CommentVisibility;
  /** New audience (member ids), sent only when `visibility === "shared"`. */
  shared_with?: number[];
}
/** PATCH /api/comments/:cid/visibility → { id, visibility, shared_with }. */
export interface VisibilityChangeResponse {
  id: number;
  visibility: CommentVisibility;
  shared_with: MentionRef[];
}

// ── notifications (per-user inbox — mention / reply / status-change) ───────
export type NotificationKind =
  | "mention"
  | "reply"
  | "status-change"
  | "assignment";

/** One inbox row. `source_ref` is the permalink the bell deep-links to
 *  (`/w/<slug>/doc/<docId>?c=<commentId>`); `read_at` is null while unread.
 *  Named `NotificationItem` (not `Notification`) to avoid the DOM global. */
export interface NotificationItem {
  id: number;
  kind: NotificationKind | string;
  source_ref: string;
  summary: string;
  read_at: string | null;
  created_at: string;
}
/** GET /api/notifications → the caller's rows (unread-first) + unread count. */
export interface NotificationFeed {
  unread_count: number;
  notifications: NotificationItem[];
}
/** POST /api/notifications/:id/read → { id, read_at }. */
export interface NotificationMark {
  id: number;
  read_at: string;
}
/** POST /api/notifications/read-all → { marked }. */
export interface NotificationMarkAll {
  marked: number;
}

// ── people ──────────────────────────────────────────────────────────────
export interface PersonMembership {
  user: User;
  role: string;
  /**
   * article-first People sweep (IA §5) — ADDITIVE, OPTIONAL per-member
   * contribution context, computed server-side over live tables (no migration).
   * `article_unit_count` = distinct `el.article_units` the member touched (a
   * comment on a doc in the unit, or a section-version they edited);
   * `open_comment_count` = the member's own open annotations in this workspace.
   * Optional because the role-change endpoint returns a `PersonMembership`
   * WITHOUT the aggregates — the People row treats them as absent ⇒ no chip.
   */
  article_unit_count?: number;
  open_comment_count?: number;
}

export interface PeopleInviteRequest {
  email: string;
  role: string;
}

// ── admin user management + permission matrix (staff / ops plane) ──────────
// Mirrors the Zig `AdminOps` wire shapes (src/ops/admin_ops.zig). The real
// adapter targets `/api/admin/*` (served by `elaborate-ops`, Tailscale-only /
// staff-gated; reverse-proxied in front of the SPA). The mock adapter is fully
// clickable with no backend.

/** A cross-workspace user row (the admin list + the target of every mutation). */
export interface AdminUser {
  id: string;
  name: string;
  email: string;
  is_staff: boolean;
  /** 'active' | 'suspended' | 'deactivated'. */
  status: string;
  /** 'password' | 'sso' | 'invited' — the reset-eligibility hint. */
  auth_method: string;
}

/** One matrix cell: `allowed` = overlay override ?? static default. */
export interface PermissionCell {
  capability: string;
  allowed: boolean;
  /** True when the cell reflects the STATIC default (no overlay row). */
  is_default: boolean;
  /** True for the owner row (locked-on, not toggleable). */
  locked: boolean;
}

/** One matrix row (a role and its capability cells). */
export interface PermissionRow {
  role: string;
  cells: PermissionCell[];
}

/** The full permission matrix for a workspace (roles × capabilities). */
export interface PermissionMatrix {
  workspace_id: number;
  roles: PermissionRow[];
}

/** A single matrix-cell toggle (the PUT body + echo). */
export interface PermissionToggle {
  role: string;
  capability: string;
  allowed: boolean;
}

/** The outcome of an admin-initiated password reset. */
export type ResetPasswordResponse =
  | { reset_url: string; sso_managed?: false }
  | { sso_managed: true; reset_url?: undefined };

export interface PeopleInviteResponse {
  invite_url: string;
}

// ── health ──────────────────────────────────────────────────────────────
export interface Health {
  ok: boolean;
  /**
   * The commit the RUNNING BACKEND binary was built from — `build_stamp.sha`,
   * baked in by `build.zig` at configure time. Optional on the wire because a
   * backend deployed BEFORE this field existed answers without it; a missing
   * value normalises to `unknown` rather than being displayed as a version
   * (see `lib/buildStamp.ts`). That case is real during a rollout, which is
   * exactly why it must not silently render as something plausible.
   */
  version?: string;
}

// ── error envelope ──────────────────────────────────────────────────────
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export const DOC_FILTERS = {
  folder: undefined as string | undefined,
  status: undefined as DocStatus | undefined,
};

export interface DocumentQuery {
  folder?: string;
  status?: DocStatus;
}
